/**
 * globe.ts
 *
 * Builds the terrain mesh (displaced by REAL NOAA ETOPO1 elevation data,
 * matched to the same height-exaggeration factor used by the atmosphere
 * level shells), a conformal surface-color shell just above the terrain
 * (showing MSLP or near-surface temperature -- see SurfaceColorMode), and
 * a real-vector coastline overlay for geographic reference. All layers are
 * kept at the SAME exaggeration so their relative heights stay physically
 * matched, as requested.
 *
 * The mesh itself is a "reduced" lat/lon grid (see geometryUtils.ts):
 * resolution is highest at the equator and tapers toward the poles, instead
 * of a uniform-degree grid that wastes density at the poles.
 */

import * as THREE from "three";
import { buildReducedLatLonGrid, latLonToUnitVector } from "./geometryUtils";
import type { MslpSamplerFn, WindSamplerFn } from "../physics/windSampler";
import { hypsometricColor, mslpToColor, temperatureToColor } from "../colorScales";
import { loadCoastlinePolylines } from "../data/coastlines";
import { EARTH_RADIUS_UNITS, KM_PER_SCENE_UNIT } from "../constants";

/** Latitude rows, biased toward the equator (see buildReducedLatLonGrid). */
const LAT_SEGMENTS = 400;
/** Longitude samples at the equator (the densest row); ~0.3 degree resolution. */
const EQUATOR_LON_SEGMENTS = 1200;
const MIN_LON_SEGMENTS = 6;
const EQUATORIAL_BIAS = 1.6;

/** Scene-unit radial gap above the (exaggerated) terrain surface for each overlay layer. */
const MSLP_GAP_UNITS = 0.02;
const COASTLINE_GAP_UNITS = 0.03;

/** Pressure level (hPa) used to sample "surface" air temperature for the globe's color shell. */
const SURFACE_TEMPERATURE_HPA = 1000;

/** The globe's color overlay shell can show mean-sea-level pressure, near-surface air
 *  temperature, or be hidden entirely -- the two color modes share one mesh (just swapping
 *  which precomputed vertex-color buffer is bound), since only one makes sense to show at once. */
export type SurfaceColorMode = "none" | "mslp" | "temperature";

export interface GlobeHandles {
  group: THREE.Group;
  setHeightExaggeration(factor: number): void;
  setSurfaceColorMode(mode: SurfaceColorMode): void;
  setCoastlinesVisible(visible: boolean): void;
}

export async function createGlobe(
  sampleMslpHpa: MslpSamplerFn,
  sampleWind: WindSamplerFn,
  elevationMetersAt: (latDeg: number, lonDeg: number) => number
): Promise<GlobeHandles> {
  const grid = buildReducedLatLonGrid({
    latSegments: LAT_SEGMENTS,
    equatorLonSegments: EQUATOR_LON_SEGMENTS,
    minLonSegments: MIN_LON_SEGMENTS,
    equatorialBias: EQUATORIAL_BIAS
  });
  const { vertexCount } = grid;
  console.info(`Earth3D globe mesh: ${vertexCount} vertices, ${grid.triangleCount} triangles.`);

  const elevationKm = new Float32Array(vertexCount);
  const terrainColors = new Float32Array(vertexCount * 3);
  const mslpColors = new Float32Array(vertexCount * 3);
  const surfaceTempColors = new Float32Array(vertexCount * 3);
  const colorTmp = new THREE.Color();

  for (let i = 0; i < vertexCount; i++) {
    const latDeg = grid.latLonPairs[i * 2];
    const lonDeg = grid.latLonPairs[i * 2 + 1];

    const elevKm = elevationMetersAt(latDeg, lonDeg) / 1000;
    elevationKm[i] = elevKm;
    hypsometricColor(elevKm, colorTmp);
    terrainColors[i * 3] = colorTmp.r;
    terrainColors[i * 3 + 1] = colorTmp.g;
    terrainColors[i * 3 + 2] = colorTmp.b;

    const mslpHpa = sampleMslpHpa(latDeg, lonDeg);
    mslpToColor(mslpHpa, colorTmp);
    mslpColors[i * 3] = colorTmp.r;
    mslpColors[i * 3 + 1] = colorTmp.g;
    mslpColors[i * 3 + 2] = colorTmp.b;

    const surfaceTempK = sampleWind(latDeg, lonDeg, SURFACE_TEMPERATURE_HPA).temperatureK;
    temperatureToColor(surfaceTempK, colorTmp);
    surfaceTempColors[i * 3] = colorTmp.r;
    surfaceTempColors[i * 3 + 1] = colorTmp.g;
    surfaceTempColors[i * 3 + 2] = colorTmp.b;
  }

  /** Returns the exaggerated radius (scene units) of vertex i at a given exaggeration factor.
   *  Clamped to sea level (never below it) -- ocean bathymetry (trenches, basins) is real
   *  elevation data used for the hypsometric OCEAN COLOR above, but displaying it as actual
   *  geometric dips in the terrain mesh would be physically misleading for an atmospheric
   *  visualization: air currents interact with the flat ocean SURFACE, not the sea floor many
   *  km below it. Flattening here (not in elevationKm[i] itself, which the color lookup above
   *  still reads unclamped) keeps the ocean's blue color grading intact while removing the
   *  fake trench/basin dips from the rendered geometry. */
  function terrainRadiusUnits(i: number, factor: number): number {
    return EARTH_RADIUS_UNITS + (Math.max(0, elevationKm[i]) * factor) / KM_PER_SCENE_UNIT;
  }

  // --- Terrain mesh -----------------------------------------------------------
  // Material is double-sided as a safety net: the reduced grid's row-stitching
  // triangulation keeps vertex winding consistent in the common case, but
  // rendering both sides guarantees no faces are ever accidentally culled.

  const terrainGeometry = new THREE.BufferGeometry();
  terrainGeometry.setIndex(new THREE.BufferAttribute(grid.indices, 1));
  terrainGeometry.setAttribute("position", new THREE.BufferAttribute(new Float32Array(vertexCount * 3), 3));
  terrainGeometry.setAttribute("color", new THREE.BufferAttribute(terrainColors, 3));
  const terrainMaterial = new THREE.MeshStandardMaterial({
    vertexColors: true,
    roughness: 0.9,
    metalness: 0,
    side: THREE.DoubleSide
  });
  const terrainMesh = new THREE.Mesh(terrainGeometry, terrainMaterial);

  // --- Surface color shell (conformal to terrain + a small fixed gap) -------
  // Shows either MSLP or near-surface temperature (see setSurfaceColorMode below) -- both
  // share this one mesh/geometry, swapping which precomputed color buffer is bound, since only
  // one mode is ever shown at a time.

  const mslpGeometry = new THREE.BufferGeometry();
  mslpGeometry.setIndex(new THREE.BufferAttribute(grid.indices, 1));
  mslpGeometry.setAttribute("position", new THREE.BufferAttribute(new Float32Array(vertexCount * 3), 3));
  mslpGeometry.setAttribute("color", new THREE.BufferAttribute(mslpColors, 3));
  const mslpMaterial = new THREE.MeshBasicMaterial({
    vertexColors: true,
    transparent: true,
    opacity: 0.88,
    side: THREE.DoubleSide,
    // A semi-transparent overlay should not write to the depth buffer, or it can occlude
    // things rendered after it (e.g. low-altitude wind particles) even though it's only a
    // translucent color wash, not a solid surface.
    depthWrite: false
  });
  const mslpMesh = new THREE.Mesh(mslpGeometry, mslpMaterial);
  // Explicit render order (not just depthWrite:false) is needed because Three.js sorts
  // transparent objects by camera distance, which can otherwise flip which of the MSLP
  // shell / particles draws "on top" depending on view angle. Lower draws first/behind.
  mslpMesh.renderOrder = 1;

  // --- Coastlines (real Natural Earth vectors, conformal to terrain + gap) --

  const coastlineGroup = new THREE.Group();
  const coastlineLines: Array<{
    geometry: THREE.BufferGeometry;
    unitPositions: Float32Array;
    elevationKm: Float32Array;
    pointCount: number;
  }> = [];

  try {
    const polylines = await loadCoastlinePolylines();
    const coastlineMaterial = new THREE.LineBasicMaterial({
      color: 0x111111,
      transparent: true,
      opacity: 0.55,
      depthWrite: false
    });
    const tmp = new THREE.Vector3();

    for (const line of polylines) {
      const pointCount = line.length;
      const unitPositions = new Float32Array(pointCount * 3);
      const lineElevationKm = new Float32Array(pointCount);

      for (let p = 0; p < pointCount; p++) {
        const [lonDeg, latDeg] = line[p];
        latLonToUnitVector(latDeg, lonDeg, tmp);
        unitPositions[p * 3] = tmp.x;
        unitPositions[p * 3 + 1] = tmp.y;
        unitPositions[p * 3 + 2] = tmp.z;
        // Clamped to sea level, same reasoning as terrainRadiusUnits() above -- this value is
        // only ever used to position the coastline overlay radially, never for color, so there
        // is no color-grading tradeoff to preserve here.
        lineElevationKm[p] = Math.max(0, elevationMetersAt(latDeg, lonDeg) / 1000);
      }

      const lineGeometry = new THREE.BufferGeometry();
      lineGeometry.setAttribute("position", new THREE.BufferAttribute(new Float32Array(pointCount * 3), 3));
      const lineObject = new THREE.Line(lineGeometry, coastlineMaterial);
      lineObject.renderOrder = 2;
      coastlineGroup.add(lineObject);
      coastlineLines.push({ geometry: lineGeometry, unitPositions, elevationKm: lineElevationKm, pointCount });
    }
  } catch (err) {
    console.warn("Coastline overlay unavailable (continuing without it):", err);
  }

  // --- Shared exaggeration update --------------------------------------------

  function setHeightExaggeration(factor: number): void {
    const terrainPos = terrainGeometry.getAttribute("position") as THREE.BufferAttribute;
    const mslpPos = mslpGeometry.getAttribute("position") as THREE.BufferAttribute;

    for (let i = 0; i < vertexCount; i++) {
      const ux = grid.positions[i * 3];
      const uy = grid.positions[i * 3 + 1];
      const uz = grid.positions[i * 3 + 2];

      const terrainRadius = terrainRadiusUnits(i, factor);
      terrainPos.setXYZ(i, ux * terrainRadius, uy * terrainRadius, uz * terrainRadius);

      const mslpRadius = terrainRadius + MSLP_GAP_UNITS;
      mslpPos.setXYZ(i, ux * mslpRadius, uy * mslpRadius, uz * mslpRadius);
    }
    terrainPos.needsUpdate = true;
    mslpPos.needsUpdate = true;
    terrainGeometry.computeVertexNormals();
    mslpGeometry.computeVertexNormals();

    for (const line of coastlineLines) {
      const posAttr = line.geometry.getAttribute("position") as THREE.BufferAttribute;
      for (let p = 0; p < line.pointCount; p++) {
        const ux = line.unitPositions[p * 3];
        const uy = line.unitPositions[p * 3 + 1];
        const uz = line.unitPositions[p * 3 + 2];
        const radius = EARTH_RADIUS_UNITS + (line.elevationKm[p] * factor) / KM_PER_SCENE_UNIT + COASTLINE_GAP_UNITS;
        posAttr.setXYZ(p, ux * radius, uy * radius, uz * radius);
      }
      posAttr.needsUpdate = true;
    }
  }

  setHeightExaggeration(1);

  const group = new THREE.Group();
  group.add(terrainMesh);
  group.add(mslpMesh);
  group.add(coastlineGroup);

  return {
    group,
    setHeightExaggeration,
    setSurfaceColorMode: (mode: SurfaceColorMode) => {
      mslpMesh.visible = mode !== "none";
      if (mode === "temperature") {
        mslpGeometry.setAttribute("color", new THREE.BufferAttribute(surfaceTempColors, 3));
      } else if (mode === "mslp") {
        mslpGeometry.setAttribute("color", new THREE.BufferAttribute(mslpColors, 3));
      }
    },
    setCoastlinesVisible: (visible: boolean) => {
      coastlineGroup.visible = visible;
    }
  };
}
